import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../../utils/motionVariants";
import { Link } from "react-router-dom";
import Classes from "./Services.module.css";

const features = [
    {
        icon: "/assets/icons/dryer.png",
        title: "Dryer Vent Cleaning",
        desc: "Professional removal of lint and debris to improve airflow, increase efficiency, and reduce the risk of dryer fires at home.",
        accent: "red",
        path: "/dryer-vent-cleaning",
    },
    {
        icon: "/assets/icons/fridge.png",
        title: "Appliance Repair",
        desc: "Fast and reliable repairs for dryers, washers, refrigerators, ovens, dishwashers, and other essential household appliances.",
        accent: "gray",
        path: "/appliance-repair",
    },
    {
        icon: "/assets/icons/time.png",
        title: "Quick Service",
        path: "/contact",
        linkLabel: "Get a service scheduled today",
        desc: "Prompt, dependable appliance repairs designed to get your home back up and running without long delays.",
        accent: "gray",
    },
];

export default function Services() {
    const sectionRef = useRef(null);

    // Block Framer until user scrolls at least once
    const [scrollStarted, setScrollStarted] = useState(false);
    useEffect(() => {
        const onScroll = () => {
            if (window.scrollY > 0) {
                setScrollStarted(true);
                window.removeEventListener("scroll", onScroll);
            }
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <motion.section
            id="services"
            ref={sectionRef}
            className={Classes.hero}
            variants={stagger}
            initial="hidden"
            animate={scrollStarted ? "visible" : "hidden"}
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
        >
            {/* heading */}
            <motion.div variants={fadeUp(0)} className={Classes.miniText}>
                Services
            </motion.div>

            <motion.h2 variants={fadeUp(0.1)} className={Classes.subheading}>
                Full-Service Dryer Vent &amp; Appliance Services
            </motion.h2>

            <motion.p variants={fadeUp(0.2)} className={Classes.text}>
                From expert dryer vent cleaning to reliable appliance repairs, we deliver
                safe, efficient service you can trust.
            </motion.p>

            {/* cards */}
            <motion.div className={Classes.cardsCon} variants={stagger}>
                {features.map((item, i) => (
                    <motion.article
                        key={item.title}
                        className={`${Classes.card} ${item.accent === "red" ? Classes.cardRed : Classes.cardGray
                            }`}
                        variants={fadeUp(i * 0.15)}
                        whileHover={{ y: -6 }}
                        transition={{
                            type: "spring",
                            stiffness: 90,
                            damping: 16,
                            mass: 0.7,
                        }}
                    >
                        <div className={Classes.cardBody}>
                            <h3 className={Classes.cardTitle}>{item.title}</h3>
                            <p className={Classes.cardDesc}>{item.desc}</p>
                            {item.path && (
                                <Link to={item.path} className={Classes.cardLink}>
                                    {item.linkLabel || `Learn more about ${item.title.toLowerCase()}`} →
                                </Link>
                            )}
                        </div>

                        {/* single PNG icon */}
                        <div className={Classes.iconWrap} aria-hidden="true">
                            <img
                                className={Classes.cardIcon}
                                src={item.icon}
                                alt=""
                                draggable={false}
                                loading="lazy"
                            />
                        </div>
                    </motion.article>
                ))}
            </motion.div>

            {/* CTA */}
            <a
                className={Classes.ctaLink}
                href="https://book.housecallpro.com/book/Quick-Appliance-Services-and-Dryer-Vent-Cleaning/1d772c9908b14627bf68d004ecf2882d"
                target="_blank"
                rel="noreferrer"
            >
                <button className={Classes.startBtn} type="button">
                    Schedule your service
                </button>
            </a>
        </motion.section>
    );
}
